import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Tooltip,
  Paper,
  Chip,
  Divider,
  InputAdornment,
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import GetAppIcon from '@mui/icons-material/GetApp';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';

import config from '../../src/config/config';
import axios from 'axios';

const FileActionModal = ({
  open,
  onClose,
  files = [],
  onDownloadAll,
  showDownloadAll = true,
  autoPreviewOnSelect = true,
  initialFileId = null,
}) => {

  const apiUrl = config.apiBaseUrl;

  const [selectedFile, setSelectedFile] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [fullScreen, setFullScreen] = useState(false);

  const [xmlContent, setXmlContent] = useState('');
  const [xmlLoading, setXmlLoading] = useState(false);
  const [xmlError, setXmlError] = useState('');

  const xmlAbortRef = useRef(null);

  const getExtension = useCallback((filename = '') => {
    return filename.split('.').pop()?.toLowerCase() || '';
  }, []);

  const isOfficeFile = useCallback((file) => {
    const ext = getExtension(file?.filename || '');
    return ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext);
  }, [getExtension]);

  const isXmlFile = useCallback((file) => {
    if (!file) return false;

    const ext = getExtension(file?.filename || '');
    const mime = String(file.fileType || '').toLowerCase();

    return (
      ext === 'xml' ||
      mime === 'application/xml' ||
      mime === 'text/xml' ||
      (mime === 'application/octet-stream' && ext === 'xml')
    );
  }, [getExtension]);

  const isImageFile = useCallback((file) => {
    return !!file?.fileType?.startsWith('image/');
  }, []);

  const isPdfFile = useCallback((file) => {
    return file?.fileType === 'application/pdf';
  }, []);

  const canPreview = useCallback((file) => {
    return !!file && (
      isImageFile(file) ||
      isPdfFile(file) ||
      isOfficeFile(file) ||
      isXmlFile(file)
    );
  }, [isImageFile, isPdfFile, isOfficeFile, isXmlFile]);

  const getFileKey = useCallback((file) => {
    if (!file) return null;
    return file.id ?? `${file.filename || 'archivo'}__${file.previewUrl || file.downloadUrl || ''}`;
  }, []);

  const categoryLabel = useCallback((cat) => {
    switch (cat) {
      case 'OC':
      case 'ORDENES DE COMPRA':
      case 'ORDEN DE COMPRA':
        return 'Órdenes de compra';
      case 'FACTURA':
      case 'FACTURAS':
        return 'Facturas';
      case 'DATOS BANCARIOS':
      case 'CARATULA':
        return 'Datos Bancarios (Carátula)';
      case 'COMPROBANTE':
      case 'COMPROBANTE DE PAGO':
      case 'COMPROBANTES':
      case 'COMPROBANTES DE PAGO':
      case 'COMPROBANTE TESORERO':
        return 'Comprobantes de pago';
      default:
        return 'Adjuntos generales';
    }
  }, []);

  const fileIcon = useCallback((file = {}) => {
    if (isXmlFile(file)) return <ArticleOutlinedIcon />;
    if (isImageFile(file)) return <ImageIcon />;
    if (isPdfFile(file)) return <PictureAsPdfIcon />;
    if (isOfficeFile(file)) return <DescriptionIcon />;
    return <InsertDriveFileOutlinedIcon />;
  }, [isXmlFile, isImageFile, isPdfFile, isOfficeFile]);

  const fileTypeLabel = useCallback((file = {}) => {
    if (isXmlFile(file)) return 'XML';
    if (isImageFile(file)) return 'Imagen';
    if (isPdfFile(file)) return 'PDF';
    if (isOfficeFile(file)) return 'Office';
    return file.fileType || 'Archivo';
  }, [isXmlFile, isImageFile, isPdfFile, isOfficeFile]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return files;

    return files.filter((f) => {
      const filename = String(f.filename || '').toLowerCase();
      const category = String(f.category || '').toLowerCase();
      const fileType = String(f.fileType || '').toLowerCase();

      return (
        filename.includes(q) ||
        category.includes(q) ||
        fileType.includes(q)
      );
    });
  }, [files, searchTerm]);

  const grouped = useMemo(() => {
    const map = new Map();
    const seenFilenames = new Set();

    // Función que normaliza variantes de una misma categoría a una clave canónica única
    const canonicalKey = (rawCat) => {
      const up = (rawCat || 'OTROS').toString().trim().toUpperCase();
      if (['COMPROBANTE', 'COMPROBANTE DE PAGO', 'COMPROBANTES', 'COMPROBANTES DE PAGO', 'COMPROBANTE TESORERO'].includes(up)) {
        return 'COMPROBANTE';
      }
      if (['OC', 'ORDENES DE COMPRA', 'ORDEN DE COMPRA'].includes(up)) return 'OC';
      if (['FACTURA', 'FACTURAS'].includes(up)) return 'FACTURA';
      if (['DATOS BANCARIOS', 'CARATULA', 'CARATULAS'].includes(up)) return 'DATOS BANCARIOS';
      return up;
    };

    for (const f of filtered) {
      const key = canonicalKey(f.category);
      const filename = (f.filename || '').toLowerCase().trim();

      // Deduplicar archivos con el mismo nombre (mismo archivo desde dos tablas distintas)
      if (filename && seenFilenames.has(filename)) continue;
      if (filename) seenFilenames.add(filename);

      if (!map.has(key)) map.set(key, []);
      map.get(key).push(f);
    }

    const order = ['OC', 'FACTURA', 'DATOS BANCARIOS', 'COMPROBANTE', 'OTROS'];
    const entries = Array.from(map.entries());
    entries.sort((a, b) => {
      const ia = order.indexOf(a[0]);
      const ib = order.indexOf(b[0]);
      return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    });

    return entries;
  }, [filtered]);

  const selectFile = useCallback((file) => {
    setSelectedFile(file || null);
  }, []);

  useEffect(() => {
    if (open) {
      setSearchTerm('');
      setFullScreen(false);

      if (files.length) {
        let found = null;
        if (initialFileId) {
          const targetIds = Array.isArray(initialFileId) 
            ? initialFileId.map(id => String(id)) 
            : [String(initialFileId)];
          
          found = files.find(f => targetIds.includes(String(f.id)));
        }
        selectFile(found || files[0]);
      } else {
        selectFile(null);
      }

    } else {
      setXmlContent('');
      setXmlLoading(false);
      setXmlError('');

      if (xmlAbortRef.current) {
        xmlAbortRef.current.abort();
        xmlAbortRef.current = null;
      }
    }
  }, [open, files, initialFileId, selectFile]);

  useEffect(() => {
    if (!open) return;

    if (!filtered.length) {
      selectFile(null);
      return;
    }

    const selectedKey = getFileKey(selectedFile);
    const exists = filtered.some((f) => getFileKey(f) === selectedKey);

    if (!selectedFile || !exists) {
      selectFile(filtered[0]);
    }
  }, [open, filtered, selectedFile, selectFile, getFileKey]);

  useEffect(() => {
    const loadXml = async () => {
      if (!selectedFile || !isXmlFile(selectedFile) || !selectedFile.previewUrl) {
        setXmlContent('');
        setXmlError('');
        setXmlLoading(false);
        return;
      }

      if (xmlAbortRef.current) {
        xmlAbortRef.current.abort();
      }

      const controller = new AbortController();
      xmlAbortRef.current = controller;

      try {
        setXmlLoading(true);
        setXmlError('');
        setXmlContent('');

        const response = await fetch(selectedFile.previewUrl, {
          method: 'GET',
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`No se pudo leer el XML (${response.status})`);
        }

        const text = await response.text();

        if (!controller.signal.aborted) {
          setXmlContent(text || '');
        }
      } catch (error) {
        if (error?.name === 'AbortError') return;

        console.error('Error al cargar XML:', error);
        setXmlError('No se pudo cargar la vista previa del XML');
        setXmlContent('');
      } finally {
        if (!controller.signal.aborted) {
          setXmlLoading(false);
        }
      }
    };

    loadXml();

    return () => {
      if (xmlAbortRef.current) {
        xmlAbortRef.current.abort();
      }
    };
  }, [selectedFile, isXmlFile]);

  const formatXml = useCallback((xml) => {
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xml, 'application/xml');

      const parseError = xmlDoc.getElementsByTagName('parsererror');
      if (parseError.length > 0) return xml;

      const serializer = new XMLSerializer();
      const raw = serializer.serializeToString(xmlDoc);

      let formatted = '';
      const reg = /(>)(<)(\/*)/g;
      const xmlString = raw.replace(reg, '$1\r\n$2$3');
      let pad = 0;

      xmlString.split('\r\n').forEach((node) => {
        let indent = 0;

        if (node.match(/^<\/[\w:-]+/)) {
          if (pad > 0) pad -= 1;
        } else if (node.match(/^<[\w:-]+[^>]*[^/]>.*$/)) {
          indent = 1;
        }

        formatted += `${'  '.repeat(pad)}${node}\r\n`;
        pad += indent;
      });

      return formatted.trim();
    } catch (error) {
      console.error('Error al formatear XML:', error);
      return xml;
    }
  }, []);

  const escapeHtml = useCallback((str = '') => {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }, []);

  const highlightXml = useCallback((xml) => {
    const escaped = escapeHtml(xml);

    return escaped.replace(
      /(&lt;\/?)([\w:-]+)(.*?)(\/?&gt;)/g,
      (_, openTag, tagName, attrs, closeTag) => {
        const formattedAttrs = attrs.replace(
          /([\w:-]+)=(".*?")/g,
          '<span class="xml-attr-name">$1</span>=<span class="xml-attr-value">$2</span>'
        );

        return `<span class="xml-bracket">${openTag}</span><span class="xml-tag">${tagName}</span>${formattedAttrs}<span class="xml-bracket">${closeTag}</span>`;
      }
    );
  }, [escapeHtml]);

  const formattedXmlContent = useMemo(() => {
    if (!xmlContent) return '';
    return formatXml(xmlContent);
  }, [xmlContent, formatXml]);

  const highlightedXmlContent = useMemo(() => {
    if (!formattedXmlContent) return '';
    return highlightXml(formattedXmlContent);
  }, [formattedXmlContent, highlightXml]);

  const getPdfPreviewUrl = useCallback((url) => {
    if (!url) return url;
    if (url.includes('#')) return url;
    return `${url}#toolbar=1&navpanes=0&scrollbar=1&view=FitH`;
  }, []);

  const getOfficeViewerUrl = useCallback((url) => {
    if (!url) return url;
    return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
  }, []);

  const handleDownload = useCallback(async () => {
    if (!selectedFile?.downloadUrl) return;

    try {
      const response = await fetch(selectedFile.downloadUrl);

      if (!response.ok) {
        throw new Error(`No se pudo descargar el archivo (${response.status})`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = selectedFile.filename || 'archivo';

      document.body.appendChild(link);
      link.click();
      link.remove();

      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al descargar archivo:', error);
    }
  }, [selectedFile]);

  const handleDownloadAll = useCallback(async () => {
    try {
      const ids = filtered
        .map((f) => f.id)
        .filter((id) => id !== undefined && id !== null);

      if (!ids.length) {
        console.warn('No hay IDs válidos para descargar.');
        return;
      }

      const response = await axios.post(
        `${apiUrl}/descargarTodos`,
        { ids },
        { responseType: 'blob' }
      );

      let filename = 'adjuntos.zip';
      const cd = response.headers['content-disposition'];

      if (cd) {
        const match = cd.match(/filename\*?=(?:UTF-8'')?"?([^"]+)"?/i);
        if (match?.[1]) {
          filename = decodeURIComponent(match[1].replace(/"/g, '').trim());
        }
      }

      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error al descargar ZIP:', err);
    }
  }, [filtered, apiUrl]);

  const shouldShowPreview = autoPreviewOnSelect && canPreview(selectedFile);

  const renderEmptyPreview = (message) => (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'grid',
        placeItems: 'center',
        textAlign: 'center',
        px: 3,
      }}
    >
      <Box>
        <InsertDriveFileOutlinedIcon sx={{ fontSize: 42, color: 'text.disabled', mb: 1 }} />
        <Typography sx={{ fontWeight: 700, color: 'text.primary' }}>
          {message}
        </Typography>
      </Box>
    </Box>
  );

  const renderXmlPreview = () => {
    if (xmlLoading) {
      return (
        <Box sx={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center' }}>
          <Typography sx={{ color: '#cbd5e1', fontWeight: 700 }}>
            Cargando XML...
          </Typography>
        </Box>
      );
    }

    if (xmlError) {
      return (
        <Box sx={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center', px: 3 }}>
          <Typography sx={{ color: '#fca5a5', textAlign: 'center', fontWeight: 700 }}>
            {xmlError}
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          overflow: 'auto',
          background: 'linear-gradient(180deg, #0f172a 0%, #111827 100%)',
          color: '#e2e8f0',
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.25,
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            background: 'rgba(17, 24, 39, 0.96)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            zIndex: 1,
            backdropFilter: 'blur(8px)',
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#cbd5e1' }}>
            Vista previa XML
          </Typography>

          <Chip
            size="small"
            label="XML"
            sx={{
              bgcolor: 'rgba(59,130,246,0.15)',
              color: '#93c5fd',
              fontWeight: 800,
              border: '1px solid rgba(147,197,253,0.25)',
            }}
          />
        </Box>

        <Box
          sx={{
            p: 2,
            fontSize: 13,
            lineHeight: 1.7,
            fontFamily: 'Consolas, Monaco, "Courier New", monospace',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            '& .xml-tag': { color: '#60a5fa', fontWeight: 700 },
            '& .xml-bracket': { color: '#94a3b8' },
            '& .xml-attr-name': { color: '#f59e0b', fontWeight: 700 },
            '& .xml-attr-value': { color: '#86efac' },
          }}
          dangerouslySetInnerHTML={{
            __html: highlightedXmlContent || 'XML vacío',
          }}
        />
      </Box>
    );
  };

  const renderPreview = () => {
    if (!selectedFile) {
      return renderEmptyPreview('Selecciona un archivo');
    }

    if (!canPreview(selectedFile)) {
      return renderEmptyPreview('Este archivo no tiene vista previa disponible');
    }

    if (!shouldShowPreview) {
      return renderEmptyPreview('La vista previa automática está deshabilitada');
    }

    if (isImageFile(selectedFile)) {
      return (
        <Box
          component="img"
          src={selectedFile.previewUrl}
          alt={selectedFile.filename}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            backgroundColor: '#fff',
          }}
        />
      );
    }

    if (isPdfFile(selectedFile)) {
      return (
        <iframe
          src={getPdfPreviewUrl(selectedFile.previewUrl)}
          title={selectedFile.filename}
          style={{ width: '100%', height: '100%', border: 'none' }}
        />
      );
    }

    if (isXmlFile(selectedFile)) {
      return renderXmlPreview();
    }

    if (isOfficeFile(selectedFile)) {
      return (
        <iframe
          src={getOfficeViewerUrl(selectedFile.previewUrl)}
          title={selectedFile.filename}
          style={{ width: '100%', height: '100%', border: 'none' }}
        />
      );
    }

    return renderEmptyPreview('Este archivo no tiene vista previa disponible');
  };

  return (
    <Dialog
      open={open}
      disableRestoreFocus
      onClose={onClose}
      maxWidth={fullScreen ? false : 'xl'}
      fullScreen={fullScreen}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : '16px',
          overflow: 'hidden',
          boxShadow: '0 24px 48px -12px rgba(15, 23, 42, 0.25)',
          background: '#ffffff',
          maxHeight: fullScreen ? '100vh' : '88vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 'clamp(8px, 1vw, 14px)',
          px: 'clamp(12px, 1.5vw, 20px)',
          py: 'clamp(10px, 1.2vw, 14px)',
          background: 'linear-gradient(135deg, #0655A5 0%, #033C75 100%)',
          color: '#ffffff',
          boxShadow: '0 2px 8px rgba(6, 85, 165, 0.25)',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            bgcolor: 'rgba(255,255,255,0.18)',
            borderRadius: '12px',
            border: '1px solid rgba(255,255,255,0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 0.6,
          }}
        >
          <AttachFileRoundedIcon sx={{ fontSize: 'clamp(16px, 1.4vw, 20px)', color: '#ffffff' }} />
        </Paper>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 'clamp(0.85rem, 0.80rem + 0.2vw, 1rem)', lineHeight: 1.2, letterSpacing: '-0.01em' }}>
            Archivos adjuntos
          </Typography>
          <Typography sx={{ fontSize: 'clamp(0.68rem, 0.64rem + 0.1vw, 0.76rem)', opacity: 0.9, fontWeight: 500 }}>
            {files.length} archivo{files.length === 1 ? '' : 's'} disponible{files.length === 1 ? '' : 's'}
          </Typography>
        </Box>

        <Tooltip title={fullScreen ? 'Salir de pantalla completa' : 'Pantalla completa'}>
          <IconButton onClick={() => setFullScreen((v) => !v)} sx={{ color: '#ffffff', p: 0.6, '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}>
            {fullScreen ? <FullscreenExitIcon sx={{ fontSize: 'clamp(18px, 1.3vw, 22px)' }} /> : <FullscreenIcon sx={{ fontSize: 'clamp(18px, 1.3vw, 22px)' }} />}
          </IconButton>
        </Tooltip>

        <Tooltip title="Cerrar">
          <IconButton onClick={onClose} sx={{ color: '#ffffff', p: 0.6, '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}>
            <CloseIcon sx={{ fontSize: 'clamp(18px, 1.3vw, 22px)' }} />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      <DialogContent sx={{ p: 0, bgcolor: '#f8fafc' }}>
        <Box
          sx={{
            display: 'flex',
            height: fullScreen ? 'calc(100vh - 110px)' : 'clamp(420px, 68vh, 660px)',
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          {/* Panel Lateral Izquierdo */}
          <Box
            sx={{
              width: { xs: '100%', md: 'clamp(260px, 28vw, 360px)' },
              borderRight: { md: '1px solid #e2e8f0' },
              borderBottom: { xs: '1px solid #e2e8f0', md: 'none' },
              p: 'clamp(8px, 1vw, 14px)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'clamp(6px, 0.8vw, 10px)',
              bgcolor: '#ffffff',
            }}
          >
            <TextField
              size="small"
              placeholder="Buscar archivo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon sx={{ color: '#0655A5', fontSize: 'clamp(14px, 1.1vw, 17px)' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: 'clamp(28px, 2.2vw, 32px)',
                  borderRadius: '10px',
                  backgroundColor: '#f8fafc',
                  fontSize: 'clamp(0.68rem, 0.64rem + 0.1vw, 0.76rem)',
                  '& fieldset': { borderColor: '#e2e8f0' },
                  '&:hover fieldset': { borderColor: '#cbd5e1' },
                  '&.Mui-focused fieldset': { borderColor: '#0655A5' },
                },
              }}
            />

            <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
              <Chip
                size="small"
                label={`Total: ${files.length}`}
                sx={{
                  fontWeight: 700,
                  fontSize: 'clamp(0.62rem, 0.58rem + 0.08vw, 0.68rem)',
                  height: '20px',
                  bgcolor: '#f1f5f9',
                  color: '#475569',
                  borderRadius: '6px',
                }}
              />
              <Chip
                size="small"
                label={`Filtrados: ${filtered.length}`}
                sx={{
                  fontWeight: 700,
                  fontSize: 'clamp(0.62rem, 0.58rem + 0.08vw, 0.68rem)',
                  height: '20px',
                  bgcolor: 'rgba(6, 85, 165, 0.08)',
                  color: '#0655A5',
                  borderRadius: '6px',
                }}
              />
            </Box>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                backgroundColor: '#ffffff',
                boxShadow: '0 1px 3px rgba(15, 23, 42, 0.03)',
              }}
            >
              <List dense sx={{ overflowY: 'auto', height: '100%', p: 0.8 }}>
                {grouped.length > 0 ? (
                  grouped.map(([cat, catFiles], idx) => {
                    const isOtros = cat === 'OTROS';
                    
                    return (
                      <Box 
                        key={cat} 
                        sx={{ 
                          mb: isOtros ? 0 : 1.2,
                          opacity: isOtros ? 0.75 : 1,
                          p: isOtros ? 0.4 : 0,
                          borderRadius: '8px',
                          mt: isOtros ? 1 : 0,
                          border: isOtros ? '1px dashed #e2e8f0' : 'none'
                        }}
                      >
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between', 
                            width: '100%',
                            px: 1,
                            py: 0.4,
                            mb: 0.4,
                            borderRadius: '6px',
                            bgcolor: isOtros ? '#f8fafc' : 'rgba(241, 245, 249, 0.6)'
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 800,
                              fontSize: isOtros ? '0.64rem' : 'clamp(0.66rem, 0.62rem + 0.1vw, 0.74rem)',
                              color: isOtros ? '#64748b' : '#1e293b',
                              textTransform: isOtros ? 'uppercase' : 'none',
                              letterSpacing: isOtros ? '0.4px' : 'normal',
                            }}
                          >
                            {isOtros ? 'Documentación Adicional' : categoryLabel(cat)}
                          </Typography>
                          <Chip 
                            label={catFiles.length} 
                            size="small" 
                            sx={{ 
                              height: 18, 
                              fontSize: '0.62rem', 
                              fontWeight: 800, 
                              bgcolor: isOtros ? 'rgba(100, 116, 139, 0.1)' : 'rgba(6, 85, 165, 0.1)',
                              color: isOtros ? '#64748b' : '#0655A5',
                              minWidth: 20
                            }} 
                          />
                        </Box>

                        {catFiles.map((file) => {
                          const isSelected = getFileKey(selectedFile) === getFileKey(file);

                          return (
                            <ListItem key={getFileKey(file)} disablePadding sx={{ mb: 0.4 }}>
                              <ListItemButton
                                selected={isSelected}
                                onClick={() => selectFile(file)}
                                sx={{
                                  borderRadius: '8px',
                                  py: 'clamp(4px, 0.5vw, 6px)',
                                  px: 'clamp(6px, 0.8vw, 10px)',
                                  alignItems: 'center',
                                  border: isSelected
                                    ? '1px solid rgba(6, 85, 165, 0.3)'
                                    : '1px solid transparent',
                                  backgroundColor: isSelected
                                    ? 'rgba(6, 85, 165, 0.08) !important'
                                    : 'transparent',
                                  transition: 'all 0.15s ease',
                                  '&:hover': {
                                    backgroundColor: isSelected
                                      ? 'rgba(6, 85, 165, 0.12) !important'
                                      : '#f1f5f9',
                                  },
                                }}
                              >
                                <ListItemIcon 
                                  sx={{ 
                                    minWidth: isOtros ? 24 : 28, 
                                    color: isSelected ? '#0655A5' : (isOtros ? '#94a3b8' : '#64748b'),
                                    '& svg': { fontSize: isOtros ? '0.95rem' : '1.1rem' }
                                  }}
                                >
                                  {fileIcon(file)}
                                </ListItemIcon>

                                <ListItemText
                                  primary={
                                    <Typography
                                      variant="body2"
                                      sx={{ 
                                        fontWeight: isOtros ? 600 : 700, 
                                        fontSize: isOtros ? '0.70rem' : 'clamp(0.70rem, 0.66rem + 0.08vw, 0.78rem)',
                                        color: isOtros && !isSelected ? '#64748b' : '#0f172a' 
                                      }}
                                      noWrap
                                      title={file.filename || 'Sin nombre'}
                                    >
                                      {file.filename || 'Sin nombre'}
                                    </Typography>
                                  }
                                  secondary={!isOtros && (
                                    <Typography
                                      variant="caption"
                                      sx={{ fontSize: '0.62rem', color: '#64748b', display: 'block', fontWeight: 500 }}
                                      noWrap
                                    >
                                      {file.fileType || 'archivo'}
                                    </Typography>
                                  )}
                                />
                              </ListItemButton>
                            </ListItem>
                          );
                        })}

                        {idx !== grouped.length - 1 && <Divider sx={{ my: 0.8, opacity: 0.5 }} />}
                      </Box>
                    );
                  })
                ) : (
                  <Box sx={{ py: 5, textAlign: 'center', px: 2 }}>
                    <SearchRoundedIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 0.8 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                      Sin resultados para “{searchTerm}”
                    </Typography>
                  </Box>
                )}
              </List>
            </Box>
          </Box>

          {/* Panel Principal Derecho: Visualizador */}
          <Box
            sx={{
              flex: 1,
              p: 'clamp(10px, 1.2vw, 16px)',
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
            }}
          >
            {/* Cabecera del archivo actual */}
            <Box
              sx={{
                mb: 'clamp(8px, 1vw, 12px)',
                px: 'clamp(10px, 1.2vw, 14px)',
                py: 'clamp(8px, 1vw, 10px)',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                bgcolor: '#ffffff',
                boxShadow: '0 1px 3px rgba(15, 23, 42, 0.03)',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minWidth: 0,
              }}
            >
              <Box sx={{ color: '#0655A5', display: 'grid', placeItems: 'center' }}>
                {selectedFile ? fileIcon(selectedFile) : <InsertDriveFileOutlinedIcon />}
              </Box>

              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{ fontWeight: 800, fontSize: 'clamp(0.74rem, 0.70rem + 0.1vw, 0.84rem)', color: '#0f172a' }}
                  noWrap
                  title={selectedFile?.filename}
                >
                  {selectedFile?.filename || 'Sin archivo seleccionado'}
                </Typography>

                <Typography
                  variant="caption"
                  sx={{ color: '#64748b', fontSize: '0.65rem', display: 'block' }}
                  noWrap
                >
                  {selectedFile?.previewUrl || selectedFile?.downloadUrl || 'Sin ruta disponible'}
                </Typography>
              </Box>

              {selectedFile && (
                <Chip
                  size="small"
                  label={fileTypeLabel(selectedFile)}
                  sx={{
                    fontWeight: 800,
                    fontSize: '0.65rem',
                    height: '22px',
                    bgcolor: 'rgba(6,85,165,0.08)',
                    color: '#0655A5',
                    border: '1px solid rgba(6,85,165,0.16)',
                    borderRadius: '6px',
                  }}
                />
              )}
            </Box>

            {/* Contenedor del Visor */}
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                border: '1px solid #cbd5e1',
                borderRadius: '12px',
                overflow: 'hidden',
                backgroundColor: '#ffffff',
                boxShadow: 'inset 0 1px 3px rgba(15, 23, 42, 0.04)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {renderPreview()}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 'clamp(12px, 1.5vw, 20px)',
          py: 'clamp(8px, 1vw, 12px)',
          background: '#ffffff',
          borderTop: '1px solid #e2e8f0',
        }}
      >
        <Button
          variant="contained"
          size="small"
          startIcon={<GetAppIcon sx={{ fontSize: '15px !important' }} />}
          onClick={handleDownload}
          disabled={!selectedFile}
          sx={{
            borderRadius: '10px',
            fontWeight: 700,
            fontSize: 'clamp(0.68rem, 0.64rem + 0.1vw, 0.76rem)',
            bgcolor: '#0655A5',
            boxShadow: 'none',
            textTransform: 'none',
            px: 'clamp(10px, 1.2vw, 16px)',
            height: 'clamp(28px, 2.2vw, 32px)',
            '&:hover': { bgcolor: '#054785', boxShadow: 'none' },
          }}
        >
          Descargar
        </Button>

        <Box sx={{ flex: 1 }} />

        {showDownloadAll && (
          <Button
            variant="outlined"
            size="small"
            onClick={onDownloadAll ? () => onDownloadAll(filtered) : handleDownloadAll}
            disabled={filtered.length === 0}
            sx={{
              borderRadius: '10px',
              fontWeight: 700,
              fontSize: 'clamp(0.68rem, 0.64rem + 0.1vw, 0.76rem)',
              borderColor: '#cbd5e1',
              color: '#334155',
              textTransform: 'none',
              px: 'clamp(10px, 1.2vw, 16px)',
              height: 'clamp(28px, 2.2vw, 32px)',
              '&:hover': { bgcolor: '#f8fafc', borderColor: '#94a3b8' },
            }}
          >
            Descargar todo
          </Button>
        )}

        <Button
          variant="text"
          size="small"
          onClick={onClose}
          sx={{
            borderRadius: '10px',
            fontWeight: 700,
            fontSize: 'clamp(0.68rem, 0.64rem + 0.1vw, 0.76rem)',
            ml: 1,
            color: '#64748b',
            textTransform: 'none',
            height: 'clamp(28px, 2.2vw, 32px)',
            '&:hover': { bgcolor: '#f1f5f9' },
          }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FileActionModal;
